import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  Icon,
  message,
  Tooltip,
  Spin,
  Avatar,
  Modal,
  Progress
} from 'antd'
import {
  listAIVoices,
  uploadCustomVoice
} from '../../../../services/api/aiVideoGeneration'
import { config } from '../../../../constants'
import { getToken } from '../../../../services/api/user'
import StepTitle from '../StepTitle'
import VoiceSelectionModal from './VoiceSelectionModal'
import './Voiceover.scss'
import { clearSceneCurrentAudio } from '../../utils/audioHistory'
import useTaskPoller from '../../hooks/useTaskPoller'

const AUDIO_PREVIEW_BASE_URL = `${config.videoGenerationUrl.replace(
  /\/?$/,
  '/'
)}audio/`

const CharacterVoiceover = ({
  formData,
  setFormData,
  onValidationChange,
  voices,
  setVoices,
  onVoiceChange,
  videoId,
  isDraftMode = false,
  isShotsReady = true,
  shotGenerationStyle = null,
  shotsGenerationProgress = 0
}) => {
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [playingVoice, setPlayingVoice] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingVoiceId, setPendingVoiceId] = useState(null)
  const [activeTarget, setActiveTarget] = useState(null)
  const [isSelectionModalVisible, setIsSelectionModalVisible] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const [pendingUploadFile, setPendingUploadFile] = useState(null)
  const [pendingVoiceName, setPendingVoiceName] = useState('')
  const audioRef = useRef(null)

  const [audioProgress, setAudioProgress] = useState(0)
  const voiceListPoller = useTaskPoller({
    pollInterval: 3000,
    continuePollingOnError: false
  })

  useEffect(() => {
    if (voices.length === 0) {
      fetchVoices()
    }
    // Set initial active target to first character if available
    const charKeys = Object.keys(formData.characterMap || {})
    if (charKeys.length > 0 && !activeTarget) {
      setActiveTarget(charKeys[0])
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [formData.characterMap, voices.length])

  useEffect(() => {
    if (onValidationChange) {
      // Character selection is optional (defaults to narrator), but the step itself is valid once reached
      onValidationChange(true)
    }
  }, [])

  const fetchVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await listAIVoices(videoId)
      const result = await voiceListPoller.resolveTaskData(res, {
        getDirectOutput: data =>
          data && data.output !== undefined ? data.output : data,
        isDirectResponse: (data, output) => {
          const status =
            typeof data?.status === 'string' ? data.status.toLowerCase() : ''
          return (
            status === 'success' ||
            data?.success === true ||
            Array.isArray(output?.default_voices) ||
            Array.isArray(output?.uploaded_voices)
          )
        }
      })
      const data = result?.output
      if (!data) {
        setVoices([])
        return
      }
      const defaultVoices = data?.default_voices || []
      const uploadedVoices = data?.uploaded_voices || []

      const allVoices = [
        ...defaultVoices.map((voice, index) => ({
          id: voice,
          name: `Voice ${index + 1}`,
          type: 'default'
        })),
        ...uploadedVoices.map(voice => ({
          id: voice,
          name: voice,
          type: 'uploaded'
        }))
      ]
      setVoices(allVoices)
    } catch (err) {
      message.error('Failed to load voices')
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }

  const handleSelectVoice = voiceId => {
    const isValidAudioUrl = value => {
      if (typeof value !== 'string') return false
      const normalized = value.trim().toLowerCase()
      return (
        normalized.length > 0 &&
        normalized !== 'null' &&
        normalized !== 'undefined'
      )
    }

    const hasGeneratedAudio = (formData?.shotList || []).some(scene =>
      isValidAudioUrl(scene?.audio_link)
    )

    const existingVoiceId = (formData?.characterVoices || {})[activeTarget]
    const isChangingAssignedVoice =
      !!existingVoiceId && existingVoiceId !== voiceId

    if (!isDraftMode && isChangingAssignedVoice && hasGeneratedAudio) {
      setPendingVoiceId(voiceId)
      setShowConfirmationModal(true)
    } else {
      applyVoiceSelection(voiceId)
      setIsSelectionModalVisible(false)
    }
  }

  const handleOverride = () => {
    Modal.confirm({
      title: 'Confirm Override',
      content:
        'This will regenerate audio for all scenes. Are you sure you want to continue?',
      centered: true,
      okType: 'danger',
      onOk: () => {
        if (pendingVoiceId) {
          applyVoiceSelection(pendingVoiceId)
          setIsSelectionModalVisible(false)
        }
        setShowConfirmationModal(false)
        setPendingVoiceId(null)
      }
    })
  }

  const applyVoiceSelection = voiceId => {
    if (onVoiceChange) {
      onVoiceChange(voiceId, false, activeTarget)
    } else {
      setFormData(prev => ({
        ...prev,
        characterVoices: {
          ...(prev.characterVoices || {}),
          [activeTarget]: voiceId
        },
        shotList: (prev.shotList || []).map(scene => ({
          ...clearSceneCurrentAudio(scene),
          shots: scene.shots.map(shot => ({
            ...shot,
            audio_duration: null
          }))
        }))
      }))
    }
  }

  const handlePlayPreview = voiceId => {
    if (playingVoice === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingVoice(null)
      setAudioProgress(0)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
      }
      setAudioProgress(0)
      const audio = new Audio(`https://video-generate.sharelookapp.link/audio/${voiceId}`)
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setAudioProgress((audio.currentTime / audio.duration) * 100)
        }
      }
      audio.play().catch(() => message.error('Failed to play audio preview'))
      audio.onended = () => {
        setPlayingVoice(null)
        setAudioProgress(0)
      }
      audioRef.current = audio
      setPlayingVoice(voiceId)
    }
  }

  const handleUploadVoice = async options => {
    const { file } = options
    const derivedName = file?.name?.replace(/\.[^/.]+$/, '') || 'new_voice'
    setPendingUploadFile(file)
    setPendingVoiceName(derivedName)
    await uploadVoiceFile(file, derivedName, false, options)
  }

  const uploadVoiceFile = async (
    file,
    voiceName,
    overwrite = false,
    options = {}
  ) => {
    const { onSuccess, onError } = options
    if (!file) return

    const uploadFormData = new FormData()
    uploadFormData.append('voice_file', file)
    uploadFormData.append('voice_name', voiceName)
    if (overwrite) {
      uploadFormData.append('overwrite', 'true')
    }

    setUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    let progressInterval = null
    try {
      const token = await getToken()
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      await uploadCustomVoice(uploadFormData, token)
      setUploadProgress(100)
      message.success('Voice uploaded successfully!')
      setPendingUploadFile(null)
      setPendingVoiceName('')
      await fetchVoices()
      if (onSuccess) onSuccess()
    } catch (err) {
      const detail = err?.response?.data?.detail
      const apiError = err?.response?.data?.error
      let errorMsg = 'Failed to upload voice'
      if (err.response?.status === 413) {
        errorMsg = 'File is too large. Please upload a file smaller than 50MB.'
      } else if (
        typeof detail === 'string' &&
        detail.includes('already exists')
      ) {
        errorMsg = `${detail} You can edit the voice name and retry, or overwrite existing.`
      } else if (apiError) {
        errorMsg = apiError
      } else if (detail) {
        errorMsg = detail
      } else if (err.message) {
        errorMsg = err.message
      }
      setUploadError(errorMsg)
      if (onError) onError(err)
    } finally {
      if (progressInterval) clearInterval(progressInterval)
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const beforeUpload = file => {
    setUploadError(null)
    const isLt50M = file.size / 1024 / 1024 < 50
    if (!isLt50M) {
      setUploadError('File must be smaller than 50MB!')
      return false
    }
    return true
  }

  const handleRetryUpload = async () => {
    if (!pendingUploadFile || !pendingVoiceName?.trim()) return
    await uploadVoiceFile(pendingUploadFile, pendingVoiceName.trim(), false)
  }

  const handleOverwriteUpload = async () => {
    if (!pendingUploadFile || !pendingVoiceName?.trim()) return
    await uploadVoiceFile(pendingUploadFile, pendingVoiceName.trim(), true)
  }

  const characterMap = formData.characterMap || {}
  const characterVoices = formData.characterVoices || {}

  return (
    <div className="voiceover-step">
      {!isDraftMode && (
        <StepTitle
          iconType="user"
          title="Character Voices"
          description="Assign distinct voices to your story characters."
        />
      )}

      <div className="voiceover-step__content">
        <div className="voiceover-step__mapping-card">
          <div className="voiceover-step__mapping-header">
            <div>
              <h3 className="voiceover-step__mapping-title">
                Character Mapping
              </h3>
              <p className="voiceover-step__mapping-desc">
                Select specialized voices for each character identified in your
                script.
              </p>
            </div>
          </div>

          <div className="voiceover-step__mapping-sections">
            <div className="voiceover-step__mapping-section character-section">
              {isDraftMode && !isShotsReady ? (
                <div className="character-loading-state">
                  <div className="processing-visual">
                    <div className="custom-loader" />
                  </div>
                  <p>Analyzing story and identifying characters...</p>
                  {shotsGenerationProgress > 0 && (
                    <div className="shots-progress-container">
                      <Progress
                        percent={Math.round(shotsGenerationProgress)}
                        size="small"
                        status="active"
                      />
                    </div>
                  )}
                </div>
              ) : Object.keys(characterMap).length === 0 ? (
                <div className="no-characters-state">
                  <p>No characters detected in this story.</p>
                </div>
              ) : (
                <div className="voiceover-step__mapping-table-wrapper">
                  <table className="voiceover-step__mapping-table">
                    <thead>
                      <tr>
                        <th>Character</th>
                        <th>Assigned Voice</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(characterMap).map(([key, name]) => (
                        <tr
                          key={key}
                          className={activeTarget === key ? 'row--active' : ''}
                        >
                          <td>
                            <div className="target-cell">
                              <Avatar
                                size={32}
                                icon="user"
                                className={`target-avatar ${activeTarget === key
                                  ? 'target-avatar--active'
                                  : ''
                                  }`}
                              />
                              <span className="target-name">
                                {typeof name === 'object' ? name.name : name}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div
                              className={`voice-badge ${!characterVoices[key]
                                ? 'voice-badge--empty'
                                : ''
                                }`}
                            >
                              {characterVoices[key] ? (
                                <>
                                  <Icon type="check-circle" theme="filled" />
                                  <span>
                                    {voices.find(
                                      v => v.id === characterVoices[key]
                                    )?.name || 'Custom Voice'}
                                  </span>
                                </>
                              ) : (
                                <span>Narrator (Default)</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {characterVoices[key] ? (
                              <span className="status-badge status-badge--mapped">
                                <Icon type="check-circle" theme="filled" />
                                Mapped
                              </span>
                            ) : (
                              <span className="status-badge status-badge--default">
                                Default
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="action-cell">
                              {characterVoices[key] && (
                                <Button
                                  size="small"
                                  shape="circle"
                                  icon={
                                    playingVoice === characterVoices[key]
                                      ? 'pause'
                                      : 'caret-right'
                                  }
                                  onClick={() =>
                                    handlePlayPreview(characterVoices[key])
                                  }
                                  className={`preview-btn ${playingVoice === characterVoices[key]
                                    ? 'preview-btn--playing'
                                    : ''
                                    }`}
                                />
                              )}
                              <Button
                                type={
                                  activeTarget === key ? 'primary' : 'default'
                                }
                                size="small"
                                className="select-btn"
                                onClick={() => {
                                  setActiveTarget(key)
                                  setIsSelectionModalVisible(true)
                                }}
                              >
                                {characterVoices[key]
                                  ? 'Change Voice'
                                  : 'Select Voice'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <VoiceSelectionModal
        visible={isSelectionModalVisible}
        onCancel={() => setIsSelectionModalVisible(false)}
        onSelect={handleSelectVoice}
        voices={voices}
        loadingVoices={loadingVoices}
        handlePlayPreview={handlePlayPreview}
        playingVoice={playingVoice}
        audioProgress={audioProgress}
        selectedVoiceId={characterVoices[activeTarget]}
        handleUploadVoice={handleUploadVoice}
        uploading={uploading}
        uploadProgress={uploadProgress}
        uploadError={uploadError}
        setUploadError={setUploadError}
        beforeUpload={beforeUpload}
        fetchVoices={fetchVoices}
        pendingUploadFile={pendingUploadFile}
        pendingVoiceName={pendingVoiceName}
        setPendingVoiceName={setPendingVoiceName}
        onRetryUpload={handleRetryUpload}
        onOverwriteUpload={handleOverwriteUpload}
      />

      <Modal
        title="Confirm Voice Change"
        visible={showConfirmationModal}
        onOk={handleOverride}
        onCancel={() => setShowConfirmationModal(false)}
        okText="Override & Regenerate"
        cancelText="Cancel"
      >
        <p>
          Changing the voice will require regenerating audio for all scenes. Do
          you want to proceed?
        </p>
      </Modal>
    </div>
  )
}

export default CharacterVoiceover
